import React, { useState, useEffect, useRef } from 'react';
import { format, getDaysInMonth, startOfMonth, getDay } from 'date-fns';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Calendar, User, ChevronLeft, ChevronRight, Star, FileText, Sparkles, AlertTriangle, Check, Info, Clock, Save, Users, Trash2, Search } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

var API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format date as DD-MM-YYYY
function formatDateDisplay(dateStr) {
    if (!dateStr) return '';
    var parts = dateStr.split('-');
    if (parts.length === 3) {
        return parts[2] + '-' + parts[1] + '-' + parts[0];
    }
    return dateStr;
}

var ACTIVITY_TYPES = [
    { id: 'change_signature', chinese: '更換簽名', english: 'Change Signature', icon: '🖊️' },
    { id: 'signing', chinese: '簽約', english: 'Signing Contracts', icon: '✍️' },
    { id: 'opening', chinese: '開業', english: 'Opening Business', icon: '🏪' },
    { id: 'starting', chinese: '動工', english: 'Starting Projects', icon: '🚀' },
    { id: 'opening_account', chinese: '開戶', english: 'Opening Account', icon: '🏦' },
    { id: 'interview', chinese: '面試', english: 'Attend Interview', icon: '💼' },
    { id: 'meeting', chinese: '會議', english: 'Important Meeting', icon: '🤝' },
    { id: 'moving', chinese: '搬家', english: 'Moving House', icon: '🏠' }
];

var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

var YEARS = [];
for (var y = 2026; y >= 1940; y--) { YEARS.push(y.toString()); }

var DAYS = [];
for (var d = 1; d <= 31; d++) { DAYS.push(d.toString()); }

var HOURS = [];
for (var h = 0; h <= 23; h++) { HOURS.push(h.toString()); }

var MINUTES = [];
for (var m = 0; m <= 59; m++) { MINUTES.push(m.toString().padStart(2, '0')); }

function getRatingColor(level) {
    if (level === 'excellent') return 'bg-emerald-500';
    if (level === 'good') return 'bg-green-400';
    if (level === 'poor') return 'bg-amber-400';
    if (level === 'avoid') return 'bg-red-500';
    return 'bg-slate-300';
}

function getRatingBgColor(level) {
    if (level === 'excellent') return 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-300';
    if (level === 'good') return 'bg-green-50 dark:bg-green-900/30 border-green-200';
    if (level === 'poor') return 'bg-amber-50 dark:bg-amber-900/30 border-amber-200';
    if (level === 'avoid') return 'bg-red-100 dark:bg-red-900/40 border-red-300';
    return 'bg-slate-50 dark:bg-slate-800 border-slate-200';
}

function getRatingBadgeColor(level) {
    if (level === 'excellent') return 'text-lg px-3 py-1 bg-emerald-500';
    if (level === 'good') return 'text-lg px-3 py-1 bg-green-500';
    if (level === 'poor') return 'text-lg px-3 py-1 bg-amber-500';
    if (level === 'avoid') return 'text-lg px-3 py-1 bg-red-500';
    return 'text-lg px-3 py-1 bg-slate-500';
}

function CalendarDay(props) {
    var dayData = props.dayData;
    var di = props.dayIndex;
    var onSelect = props.onSelect;
    
    var rating = dayData.personal_rating;
    var isWeekend = di === 0 || di === 6;
    var dayNum = dayData.day;
    var dayPillar = dayData.pillars.day_chinese;
    var dayAnimal = dayData.pillars.day_animal;
    var ratingVal = rating.rating;
    var qualityLevel = rating.quality.level;
    var isBreaker = dayData.is_date_breaker;
    
    return (
        <button 
            onClick={function() { onSelect(dayData); }}
            className={'aspect-square p-1 rounded-lg border text-xs transition-all hover:shadow-md cursor-pointer text-left ' + getRatingBgColor(qualityLevel)}
            data-testid={'calendar-day-' + dayNum}
        >
            <div className={isWeekend ? 'font-bold text-red-500' : 'font-bold'}>{dayNum}</div>
            <div className="font-chinese text-[10px]">{dayPillar}</div>
            <div className="text-[8px] text-muted-foreground">{dayAnimal}</div>
            <div className="flex items-center justify-between mt-0.5">
                <span className={'w-2 h-2 rounded-full ' + getRatingColor(qualityLevel)} />
                <span className="text-[9px] font-medium">{ratingVal}</span>
            </div>
            {isBreaker && <div className="text-[8px] text-red-600 font-bold">日破</div>}
        </button>
    );
}

function BestDayItem(props) {
    var day = props.day;
    var index = props.index;
    var onSelect = props.onSelect;
    
    var dayDate = formatDateDisplay(day.date);
    var dayPillar = day.pillars.day_chinese;
    var dayAnimal = day.pillars.day_animal;
    var qualityChinese = day.personal_rating.quality.chinese;
    var ratingVal = day.personal_rating.rating;
    var auspiciousHours = day.auspicious_hours || [];
    
    return (
        <button 
            onClick={function() { onSelect(day); }}
            className="w-full flex flex-col p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-sm hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors text-left" 
            data-testid={'best-day-' + index}
        >
            <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xs">{index + 1}</div>
                    <div>
                        <span className="font-medium">{dayDate}</span>
                        <span className="font-chinese ml-2">{dayPillar}</span>
                        <span className="text-muted-foreground ml-1">{dayAnimal}</span>
                    </div>
                </div>
                <Badge variant="secondary" className="bg-emerald-100 dark:bg-emerald-800">{ratingVal}</Badge>
            </div>
            {auspiciousHours.length > 0 && (
                <div className="mt-2 ml-11">
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
                        <Clock className="w-3 h-3" />
                        <span>Auspicious Hours:</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {auspiciousHours.slice(0, 4).map(function(hr, i) {
                            return (
                                <span key={i} className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded">
                                    {hr.branch_chinese} {hr.time_range}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </button>
    );
}

function PersonalDateSelection() {
    var { language } = useLanguage();
    var { token, user } = useAuth();
    var isAdmin = user?.is_admin || false;
    
    var [birthYear, setBirthYear] = useState('1990');
    var [birthMonth, setBirthMonth] = useState('January');
    var [birthDay, setBirthDay] = useState('1');
    var [birthHour, setBirthHour] = useState('12');
    var [birthMinute, setBirthMinute] = useState('00');
    var [timeUnknown, setTimeUnknown] = useState(false);
    var [gender, setGender] = useState('male');
    var [userName, setUserName] = useState('');
    var [activityType, setActivityType] = useState('change_signature');
    var [targetDate, setTargetDate] = useState(new Date());
    var [personalBazi, setPersonalBazi] = useState(null);
    var [selectionData, setSelectionData] = useState(null);
    var [loading, setLoading] = useState(false);
    var [error, setError] = useState(null);
    var [selectedDay, setSelectedDay] = useState(null);
    var [exporting, setExporting] = useState(false);
    
    // Saved profiles state (Admin only)
    var [savedProfiles, setSavedProfiles] = useState([]);
    var [loadingProfiles, setLoadingProfiles] = useState(false);
    var [showProfilesDropdown, setShowProfilesDropdown] = useState(false);
    var [savingProfile, setSavingProfile] = useState(false);
    var [profileNotes, setProfileNotes] = useState('');
    var [profileSearch, setProfileSearch] = useState('');
    var dropdownRef = useRef(null);

    // Filter profiles based on search
    var filteredProfiles = savedProfiles.filter(function(profile) {
        if (!profileSearch.trim()) return true;
        var search = profileSearch.toLowerCase();
        return profile.name.toLowerCase().includes(search) ||
               (profile.notes && profile.notes.toLowerCase().includes(search));
    });

    // Close dropdown when clicking outside
    useEffect(function() {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowProfilesDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return function() {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Fetch saved profiles for admin
    useEffect(function() {
        if (isAdmin && token) {
            fetchSavedProfiles();
        }
    }, [isAdmin, token]);

    function fetchSavedProfiles() {
        setLoadingProfiles(true);
        axios.get(API_URL + '/api/auth/admin/profiles', {
            headers: { Authorization: 'Bearer ' + token }
        }).then(function(res) {
            setSavedProfiles(res.data.profiles || []);
        }).catch(function(err) {
            console.error('Failed to fetch profiles:', err);
        }).finally(function() {
            setLoadingProfiles(false);
        });
    }

    function saveCurrentProfile() {
        if (!userName.trim()) {
            alert('Please enter a name to save the profile');
            return;
        }
        setSavingProfile(true);
        axios.post(API_URL + '/api/auth/admin/profiles', {
            name: userName,
            gender: gender,
            birth_year: parseInt(birthYear),
            birth_month: getMonthNumber(birthMonth),
            birth_day: parseInt(birthDay),
            birth_hour: parseInt(birthHour),
            birth_minute: parseInt(birthMinute),
            time_unknown: timeUnknown,
            notes: profileNotes || null
        }, {
            headers: { Authorization: 'Bearer ' + token }
        }).then(function(res) {
            fetchSavedProfiles();
            alert('Profile saved successfully!');
            setProfileNotes('');
        }).catch(function(err) {
            alert('Failed to save profile: ' + (err.response?.data?.detail || err.message));
        }).finally(function() {
            setSavingProfile(false);
        });
    }

    function loadProfile(profile) {
        setUserName(profile.name);
        setGender(profile.gender);
        setBirthYear(profile.birth_year.toString());
        setBirthMonth(MONTHS[profile.birth_month - 1]);
        setBirthDay(profile.birth_day.toString());
        setBirthHour(profile.birth_hour.toString());
        setBirthMinute(profile.birth_minute.toString().padStart(2, '0'));
        setTimeUnknown(profile.time_unknown);
        setProfileNotes(profile.notes || '');
        setShowProfilesDropdown(false);
        setProfileSearch('');
        // Reset calculated data
        setPersonalBazi(null);
        setSelectionData(null);
    }

    function deleteProfile(profileId) {
        if (!window.confirm('Are you sure you want to delete this profile?')) return;
        axios.delete(API_URL + '/api/auth/admin/profiles/' + profileId, {
            headers: { Authorization: 'Bearer ' + token }
        }).then(function() {
            fetchSavedProfiles();
        }).catch(function(err) {
            alert('Failed to delete profile: ' + (err.response?.data?.detail || err.message));
        });
    }

    function getMonthNumber(monthName) {
        return MONTHS.indexOf(monthName) + 1;
    }

    function fetchDateSelection(by, bm, bd, bh, gd) {
        return axios.post(API_URL + '/api/tong-shu/personal-date-selection', {
            birth_year: by,
            birth_month: bm,
            birth_day: bd,
            birth_hour: bh,
            gender: gd,
            target_year: targetDate.getFullYear(),
            target_month: targetDate.getMonth() + 1,
            activity_type: activityType
        }).then(function(res) {
            setSelectionData(res.data);
        });
    }

    function calculateBazi() {
        if (!birthYear || !birthMonth || !birthDay) {
            setError('Please fill in all birth date fields');
            return;
        }
        setLoading(true);
        setError(null);
        
        var by = parseInt(birthYear);
        var bm = getMonthNumber(birthMonth);
        var bd = parseInt(birthDay);
        var bh = timeUnknown ? 12 : parseInt(birthHour);
        
        axios.post(API_URL + '/api/tong-shu/personal-bazi', {
            birth_year: by,
            birth_month: bm,
            birth_day: bd,
            birth_hour: bh,
            gender: gender,
            name: userName || undefined
        }).then(function(res) {
            setPersonalBazi(res.data);
            return fetchDateSelection(by, bm, bd, bh, gender);
        }).then(function() {
            setLoading(false);
        }).catch(function(err) {
            var msg = 'Failed to calculate BaZi';
            if (err.response && err.response.data && err.response.data.detail) {
                msg = err.response.data.detail;
            }
            setError(msg);
            setLoading(false);
        });
    }

    function handleMonthChange(dir) {
        var newDate = new Date(targetDate);
        newDate.setMonth(newDate.getMonth() + dir);
        setTargetDate(newDate);
        
        if (personalBazi) {
            setLoading(true);
            var by = parseInt(birthYear);
            var bm = getMonthNumber(birthMonth);
            var bd = parseInt(birthDay);
            var bh = timeUnknown ? 12 : parseInt(birthHour);
            fetchDateSelection(by, bm, bd, bh, gender)
                .then(function() { setLoading(false); })
                .catch(function() { setLoading(false); });
        }
    }

    function handleActivityChange(val) {
        setActivityType(val);
        if (personalBazi) {
            setLoading(true);
            var by = parseInt(birthYear);
            var bm = getMonthNumber(birthMonth);
            var bd = parseInt(birthDay);
            var bh = timeUnknown ? 12 : parseInt(birthHour);
            fetchDateSelection(by, bm, bd, bh, gender)
                .then(function() { setLoading(false); })
                .catch(function() { setLoading(false); });
        }
    }

    function exportToPDF() {
        if (!personalBazi || !selectionData) return;
        setExporting(true);
        var by = parseInt(birthYear);
        var bm = getMonthNumber(birthMonth);
        var bd = parseInt(birthDay);
        var bh = timeUnknown ? 12 : parseInt(birthHour);
        
        axios.post(API_URL + '/api/tong-shu/export-personal-selection', {
            birth_year: by,
            gender: gender,
            birth_month: bm,
            birth_day: bd,
            birth_hour: bh,
            target_year: targetDate.getFullYear(),
            target_month: targetDate.getMonth() + 1,
            activity_type: activityType
        }).then(function(res) {
            openPDFWindow(res.data);
            setExporting(false);
        }).catch(function() {
            setError('Failed to export');
            setExporting(false);
        });
    }

    function openPDFWindow(data) {
        var activityInfo = ACTIVITY_TYPES.find(function(a) { return a.id === activityType; });
        var monthYear = format(targetDate, 'MMMM yyyy');
        var activityLabel = activityInfo ? activityInfo.chinese + ' ' + activityInfo.english : '';
        var birthTimeStr = timeUnknown ? 'Unknown' : birthHour + ':' + birthMinute;
        
        var printWindow = window.open('', '_blank');
        if (!printWindow) return;
        
        var css = getCSSStyles();
        var headerHTML = getHeaderHTML(monthYear, activityLabel, birthTimeStr);
        var baziHTML = getBaziChartHTML(data.bazi_chart);
        var daysHTML = getRecommendedDaysHTML(data.best_days);
        var footerHTML = getFooterHTML();
        
        var html = '<!DOCTYPE html><html><head><title>Date Selection Report</title>' + css + '</head><body>';
        html += headerHTML + baziHTML + daysHTML + footerHTML;
        html += '</body></html>';
        
        printWindow.document.write(html);
        printWindow.document.close();
    }
    
    function getCSSStyles() {
        return '<style>' +
            '*{box-sizing:border-box;margin:0;padding:0}' +
            'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:40px;background:#f8fafc;color:#1e293b;line-height:1.6}' +
            '.container{max-width:800px;margin:0 auto;background:white;border-radius:16px;padding:32px;box-shadow:0 4px 24px rgba(0,0,0,0.08)}' +
            '.header{text-align:center;margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid #e2e8f0}' +
            '.logo{font-size:14px;color:#10b981;font-weight:600;letter-spacing:1px;margin-bottom:8px}' +
            '.header h1{font-size:24px;color:#0f172a;margin-bottom:6px;font-weight:700}' +
            '.subtitle{font-size:13px;color:#64748b}' +
            '.info-card{background:linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%);border:1px solid #bbf7d0;border-radius:12px;padding:16px;margin-bottom:24px}' +
            '.info-row{display:flex;flex-wrap:wrap;gap:20px;font-size:13px;color:#166534}' +
            '.info-item{display:flex;align-items:center;gap:6px}' +
            '.info-label{color:#15803d;font-weight:500}' +
            '.section{margin-bottom:28px}' +
            '.section-title{font-size:15px;font-weight:600;color:#0f172a;margin-bottom:16px;display:flex;align-items:center;gap:8px}' +
            '.section-icon{width:20px;height:20px;background:#10b981;border-radius:6px;display:flex;align-items:center;justify-content:center;color:white;font-size:11px}' +
            '.bazi-chart{display:flex;justify-content:center;gap:12px;margin:20px 0}' +
            '.pillar{text-align:center;padding:16px 20px;background:#f1f5f9;border-radius:12px;min-width:90px;border:1px solid #e2e8f0}' +
            '.pillar-day{background:linear-gradient(135deg,#fef3c7 0%,#fde68a 100%);border-color:#fbbf24}' +
            '.pillar-label{font-size:10px;color:#64748b;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px}' +
            '.pillar-chinese{font-size:26px;font-weight:700;color:#0f172a}' +
            '.day-card{background:white;border:1px solid #e2e8f0;border-radius:12px;padding:20px;margin-bottom:16px;page-break-inside:avoid}' +
            '.day-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px}' +
            '.day-date{font-size:16px;font-weight:600;color:#0f172a}' +
            '.day-pillar-info{font-size:13px;color:#64748b}' +
            '.score-box{display:flex;align-items:center;gap:12px}' +
            '.score-circle{width:48px;height:48px;background:linear-gradient(135deg,#d1fae5 0%,#a7f3d0 100%);border-radius:12px;display:flex;flex-direction:column;align-items:center;justify-content:center;border:1px solid #6ee7b7}' +
            '.score-num{font-size:18px;font-weight:700;color:#047857}' +
            '.score-label{font-size:8px;color:#059669}' +
            '.rating-badge{background:#10b981;color:white;padding:6px 14px;border-radius:20px;font-size:12px;font-weight:600}' +
            '.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px}' +
            '.info-box{background:#f8fafc;border-radius:10px;padding:12px}' +
            '.info-box-label{font-size:10px;color:#64748b;margin-bottom:4px}' +
            '.info-box-value{font-size:18px;font-weight:700;color:#0f172a}' +
            '.info-box-sub{font-size:11px;color:#64748b}' +
            '.hours-section{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:14px;margin-bottom:12px}' +
            '.hours-title{font-size:12px;font-weight:600;color:#92400e;margin-bottom:10px;display:flex;align-items:center;gap:6px}' +
            '.hours-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}' +
            '.hour-item{background:white;border:1px solid #fde68a;border-radius:8px;padding:8px;text-align:center}' +
            '.hour-chinese{font-size:14px;font-weight:700;color:#0f172a}' +
            '.hour-animal{font-size:10px;color:#64748b}' +
            '.hour-time{font-size:11px;color:#d97706;font-weight:500;margin-top:2px}' +
            '.factors-section{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px}' +
            '.factors-title{font-size:12px;font-weight:600;color:#475569;margin-bottom:10px;display:flex;align-items:center;gap:6px}' +
            '.factors-list{display:flex;flex-wrap:wrap;gap:8px}' +
            '.factor-badge{display:inline-flex;align-items:center;padding:6px 12px;border-radius:20px;font-size:11px;font-weight:500}' +
            '.factor-badge-positive{background:#fef2f2;border:1px solid #fecaca;color:#dc2626}' +
            '.factor-badge-negative{background:#fef2f2;border:1px solid #fecaca;color:#dc2626}' +
            '.footer{margin-top:28px;padding-top:16px;border-top:1px solid #e2e8f0;text-align:center}' +
            '.footer-text{font-size:11px;color:#94a3b8}' +
            '.footer-brand{font-size:12px;color:#10b981;font-weight:600;margin-top:4px}' +
            '.print-btn{display:inline-block;padding:12px 32px;background:linear-gradient(135deg,#10b981 0%,#059669 100%);color:white;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;margin-top:20px;box-shadow:0 4px 12px rgba(16,185,129,0.3)}' +
            '.print-btn:hover{transform:translateY(-1px);box-shadow:0 6px 16px rgba(16,185,129,0.4)}' +
            '@media print{body{padding:20px;background:white}.container{box-shadow:none}.no-print{display:none!important}}' +
            '</style>';
    }
    
    function getHeaderHTML(monthYear, activityLabel, birthTimeStr) {
        return '<div class="container">' +
            '<div class="header">' +
            '<div class="logo">通书 TONG SHU PLOTTER</div>' +
            '<h1>個人擇日報告</h1>' +
            '<h1 style="font-size:18px;font-weight:400;color:#64748b">Personal Date Selection Report</h1>' +
            '<div class="subtitle">' + monthYear + ' • ' + activityLabel + '</div>' +
            '</div>' +
            '<div class="info-card">' +
            '<div class="info-row">' +
            '<div class="info-item"><span class="info-label">Birth Date:</span> ' + birthMonth + ' ' + birthDay + ', ' + birthYear + '</div>' +
            '<div class="info-item"><span class="info-label">Time:</span> ' + birthTimeStr + '</div>' +
            '<div class="info-item"><span class="info-label">Gender:</span> ' + (gender === 'male' ? 'Male 男' : 'Female 女') + '</div>' +
            '</div></div>';
    }
    
    function getBaziChartHTML(chart) {
        return '<div class="section">' +
            '<div class="section-title"><div class="section-icon">☰</div>八字命盤 Four Pillars Chart</div>' +
            '<div class="bazi-chart">' +
            '<div class="pillar"><div class="pillar-label">時柱 Hour</div><div class="pillar-chinese">' + chart.hour_pillar + '</div></div>' +
            '<div class="pillar pillar-day"><div class="pillar-label">日柱 Day</div><div class="pillar-chinese">' + chart.day_pillar + '</div></div>' +
            '<div class="pillar"><div class="pillar-label">月柱 Month</div><div class="pillar-chinese">' + chart.month_pillar + '</div></div>' +
            '<div class="pillar"><div class="pillar-label">年柱 Year</div><div class="pillar-chinese">' + chart.year_pillar + '</div></div>' +
            '</div></div>';
    }
    
    function getRecommendedDaysHTML(bestDays) {
        var html = '<div class="section">' +
            '<div class="section-title"><div class="section-icon">★</div>推薦日期及良辰 Recommended Dates</div>';
        
        for (var i = 0; i < bestDays.length; i++) {
            var dd = bestDays[i];
            var formattedDate = formatDateDisplay(dd.date);
            html += '<div class="day-card">' +
                '<div class="day-header">' +
                '<div><div class="day-date">' + formattedDate + '</div>' +
                '<div class="day-pillar-info">' + dd.day_pillar + ' ' + dd.day_animal + '</div></div>' +
                '<div class="score-box">' +
                '<div class="score-circle"><div class="score-num">' + dd.rating + '</div><div class="score-label">/100</div></div>' +
                '<div class="rating-badge">' + getRatingChinese(dd.rating) + '</div>' +
                '</div></div>' +
                '<div class="info-grid">' +
                '<div class="info-box"><div class="info-box-label">Day Pillar 日柱</div>' +
                '<div class="info-box-value">' + dd.day_pillar + '</div>' +
                '<div class="info-box-sub">' + dd.day_animal + '</div></div>' +
                '<div class="info-box"><div class="info-box-label">Day Officer 建除</div>' +
                '<div class="info-box-value">' + (dd.day_officer_chinese || '—') + '</div>' +
                '<div class="info-box-sub">' + (dd.day_officer_english || '') + '</div></div>' +
                '</div>';
            
            // Auspicious Hours Section
            if (dd.auspicious_hours && dd.auspicious_hours.length > 0) {
                html += '<div class="hours-section">' +
                    '<div class="hours-title">🕐 良辰吉時 Auspicious Hours</div>' +
                    '<div class="hours-grid">';
                var maxHours = Math.min(dd.auspicious_hours.length, 6);
                for (var j = 0; j < maxHours; j++) {
                    var hr = dd.auspicious_hours[j];
                    html += '<div class="hour-item">' +
                        '<div class="hour-chinese">' + hr.branch_chinese + '</div>' +
                        '<div class="hour-animal">' + hr.branch_english + '</div>' +
                        '<div class="hour-time">' + hr.time_range + '</div></div>';
                }
                html += '</div></div>';
            }
            
            // Rating Factors Section
            if (dd.factors && dd.factors.length > 0) {
                html += '<div class="factors-section">' +
                    '<div class="factors-title">ℹ️ Rating Factors 影響因素</div>' +
                    '<div class="factors-list">';
                for (var k = 0; k < dd.factors.length; k++) {
                    var factor = dd.factors[k];
                    var isPositive = factor.type === 'positive' || factor.positive;
                    var badgeClass = isPositive ? 'factor-badge-positive' : 'factor-badge-negative';
                    var factorChinese = factor.chinese || factor;
                    var factorEnglish = factor.english || '';
                    html += '<span class="factor-badge ' + badgeClass + '">' +
                        (isPositive ? '✓ ' : '⚠ ') + factorChinese;
                    if (factorEnglish) {
                        html += ' ' + factorEnglish;
                    }
                    html += '</span>';
                }
                html += '</div></div>';
            }
            
            html += '</div>';
        }
        html += '</div>';
        return html;
    }
    
    function getRatingChinese(rating) {
        if (rating >= 85) return '上吉 Excellent';
        if (rating >= 70) return '中吉 Good';
        if (rating >= 50) return '平 Neutral';
        if (rating >= 30) return '凶 Poor';
        return '大凶 Avoid';
    }
    
    function getFooterHTML() {
        const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return '<div class="footer">' +
            '<div class="footer-text">Generated on ' + today + '</div>' +
            '<div class="footer-brand" style="font-weight:600;">通书 Online Tong Shu Plotter</div>' +
            '<div class="footer-copyright" style="font-size:11px;">Copyright © 2026 Steven Yong - Digital Dive Coaching Sdn. Bhd. All rights reserved worldwide.</div>' +
            '</div>' +
            '<div class="no-print" style="text-align:center">' +
            '<button onclick="window.print()" class="print-btn">🖨️ Print / Save as PDF</button>' +
            '</div></div>';
    }

    function renderCalendar() {
        if (!selectionData) return null;
        var days = selectionData.days;
        if (!days) return null;
        
        var firstDay = startOfMonth(targetDate);
        var startWeekday = getDay(firstDay);
        var daysInMon = getDaysInMonth(targetDate);
        var weeks = [];
        var week = [];
        
        for (var i = 0; i < startWeekday; i++) {
            week.push(null);
        }
        for (var dayNum = 1; dayNum <= daysInMon; dayNum++) {
            week.push(dayNum);
            if (week.length === 7) {
                weeks.push(week);
                week = [];
            }
        }
        while (week.length > 0 && week.length < 7) {
            week.push(null);
        }
        if (week.length > 0) {
            weeks.push(week);
        }

        var weekHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <div className="space-y-1">
                <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekHeaders.map(function(dh) {
                        return <div key={dh} className="text-center text-xs font-medium text-muted-foreground py-1">{dh}</div>;
                    })}
                </div>
                {weeks.map(function(w, wi) {
                    return (
                        <div key={wi} className="grid grid-cols-7 gap-1">
                            {w.map(function(dayNum, di) {
                                if (!dayNum) return <div key={di} className="aspect-square" />;
                                var dayData = days.find(function(x) { return x.day === dayNum; });
                                if (!dayData) return <div key={di} className="aspect-square" />;
                                return <CalendarDay key={di} dayData={dayData} dayIndex={di} onSelect={setSelectedDay} />;
                            })}
                        </div>
                    );
                })}
            </div>
        );
    }

    function renderBestDays() {
        if (!selectionData) return null;
        var bestDays = selectionData.best_days;
        if (!bestDays || bestDays.length === 0) return null;
        
        var topFive = bestDays.slice(0, 5);
        
        return (
            <div className="mt-4">
                <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    {language === 'zh' ? '推薦日期' : 'Recommended Dates'}
                    <Badge variant="secondary" className="text-xs">Top 5</Badge>
                </h3>
                <div className="space-y-2">
                    {topFive.map(function(day, i) {
                        return <BestDayItem key={i} day={day} index={i} onSelect={setSelectedDay} />;
                    })}
                </div>
            </div>
        );
    }

    function renderSummary() {
        if (!selectionData) return null;
        var summary = selectionData.summary;
        if (!summary) return null;
        
        return (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <div className="flex flex-wrap gap-4 text-sm">
                    <span className="text-emerald-600 font-medium">上吉: {summary.excellent_count}</span>
                    <span className="text-green-600 font-medium">中吉: {summary.good_count}</span>
                    <span className="text-slate-600">平: {summary.neutral_count}</span>
                    <span className="text-amber-600">凶: {summary.poor_count}</span>
                    <span className="text-red-600">大凶: {summary.avoid_count}</span>
                </div>
            </div>
        );
    }

    function renderDayDialog() {
        if (!selectedDay) return null;
        
        var rating = selectedDay.personal_rating;
        var pillars = selectedDay.pillars;
        var dayOfficer = selectedDay.day_officer;
        var factors = rating.factors;
        var isBreaker = selectedDay.is_date_breaker;
        var auspiciousHours = selectedDay.auspicious_hours || [];
        
        return (
            <Dialog open={true} onOpenChange={function() { setSelectedDay(null); }}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            {formatDateDisplay(selectedDay.date)}
                        </DialogTitle>
                        <DialogDescription>{language === 'zh' ? '日期詳情' : 'Date Details'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className={'p-4 rounded-lg border ' + getRatingBgColor(rating.quality.level)}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="text-2xl font-bold">{rating.rating}</span>
                                    <span className="text-muted-foreground ml-2">/100</span>
                                </div>
                                <Badge className={getRatingBadgeColor(rating.quality.level)}>
                                    {rating.quality.chinese} {rating.quality.english}
                                </Badge>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="p-3 bg-muted/50 rounded-lg">
                                <div className="text-muted-foreground text-xs">{language === 'zh' ? '日柱' : 'Day Pillar'}</div>
                                <div className="font-chinese font-bold text-lg">{pillars.day_chinese}</div>
                                <div className="text-muted-foreground">{pillars.day_animal}</div>
                            </div>
                            <div className="p-3 bg-muted/50 rounded-lg">
                                <div className="text-muted-foreground text-xs">{language === 'zh' ? '建除' : 'Day Officer'}</div>
                                {dayOfficer && (
                                    <div>
                                        <div className={dayOfficer.auspicious ? 'font-chinese font-bold text-lg text-emerald-600' : 'font-chinese font-bold text-lg text-red-600'}>
                                            {dayOfficer.chinese}
                                        </div>
                                        <div className="text-muted-foreground">{dayOfficer.english}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {auspiciousHours.length > 0 && (
                            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-amber-600" />
                                    {language === 'zh' ? '良辰吉時' : 'Auspicious Hours'}
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {auspiciousHours.map(function(hr, i) {
                                        return (
                                            <div key={i} className="text-xs p-2 bg-white dark:bg-background rounded border">
                                                <div className="font-chinese font-bold">{hr.branch_chinese}</div>
                                                <div className="text-muted-foreground">{hr.branch_english}</div>
                                                <div className="text-amber-700 dark:text-amber-400 font-medium">{hr.time_range}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        
                        {factors && factors.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Info className="w-4 h-4" />
                                    {language === 'zh' ? '影響因素' : 'Rating Factors'}
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {factors.map(function(f, i) {
                                        var isPositive = f.type === 'positive' || f.positive;
                                        return (
                                            <Badge key={i} variant={isPositive ? 'default' : 'destructive'} className="text-xs">
                                                {isPositive ? <Check className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                                                {f.chinese} {f.english}
                                            </Badge>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {isBreaker && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium">
                                    <AlertTriangle className="w-4 h-4" />
                                    {language === 'zh' ? '日破日 - 避免重要活動' : 'Date Breaker Day - Avoid important activities'}
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    var solarMonthInfo = null;
    if (selectionData && selectionData.days && selectionData.days.length > 0 && selectionData.days[0].solar_month) {
        solarMonthInfo = selectionData.days[0].solar_month;
    }

    return (
        <div className="container mx-auto px-4 py-6 max-w-7xl" data-testid="personal-date-selection">
            <div className="mb-6">
                <h1 className="text-2xl font-serif font-bold flex items-center gap-2">
                    <User className="w-6 h-6" />
                    {language === 'zh' ? '個人擇日' : 'Personal Date Selection'}
                </h1>
                <p className="text-muted-foreground text-sm mt-1">
                    {language === 'zh' ? '輸入您的出生日期和時間，獲取個人化的擇日建議' : 'Enter your birth date and time to get personalized auspicious date recommendations'}
                </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-1">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Calendar className="w-5 h-5" />
                                {language === 'zh' ? '出生資料' : 'Birth Information'}
                            </CardTitle>
                        </div>
                        <CardDescription>
                            {language === 'zh' ? '請輸入您的西曆出生日期和時間' : 'Please enter your Gregorian date and time of birth'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Saved Profiles Dropdown (Admin Only) */}
                        {isAdmin && savedProfiles.length > 0 && (
                            <div className="relative" ref={dropdownRef} data-testid="profiles-dropdown-container">
                                <Label className="text-amber-700 flex items-center gap-1 mb-1">
                                    <Users className="w-4 h-4" />
                                    Load Saved Profile
                                </Label>
                                <div className="relative">
                                    <Input
                                        placeholder="Search profiles by name..."
                                        value={profileSearch}
                                        onChange={function(e) { 
                                            setProfileSearch(e.target.value);
                                            setShowProfilesDropdown(true);
                                        }}
                                        onFocus={function() { setShowProfilesDropdown(true); }}
                                        className="pr-10"
                                        data-testid="profile-search-input"
                                    />
                                    <button 
                                        onClick={function() { setShowProfilesDropdown(!showProfilesDropdown); }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        <ChevronLeft className={`w-4 h-4 transition-transform ${showProfilesDropdown ? '-rotate-90' : 'rotate-0'}`} />
                                    </button>
                                </div>
                                {showProfilesDropdown && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {loadingProfiles ? (
                                            <div className="p-3 text-sm text-slate-500 text-center">Loading...</div>
                                        ) : filteredProfiles.length === 0 ? (
                                            <div className="p-3 text-sm text-slate-500 text-center">
                                                {profileSearch ? 'No profiles match your search' : 'No saved profiles'}
                                            </div>
                                        ) : (
                                            filteredProfiles.map(function(profile) {
                                                return (
                                                    <div 
                                                        key={profile.id} 
                                                        className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 cursor-pointer border-b last:border-b-0"
                                                    >
                                                        <button 
                                                            onClick={function() { loadProfile(profile); }}
                                                            className="text-left flex-1"
                                                        >
                                                            <div className="font-medium text-slate-800">{profile.name}</div>
                                                            <div className="text-xs text-slate-400 flex items-center gap-2">
                                                                <span>{profile.birth_day}/{profile.birth_month}/{profile.birth_year}</span>
                                                                <span>•</span>
                                                                <span>{profile.gender === 'male' ? '♂' : '♀'}</span>
                                                                {profile.notes && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span className="text-amber-600 truncate max-w-[100px]">{profile.notes}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </button>
                                                        <button 
                                                            onClick={function(e) { 
                                                                e.stopPropagation();
                                                                deleteProfile(profile.id); 
                                                            }}
                                                            className="text-red-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded"
                                                            title="Delete profile"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name">{language === 'zh' ? '姓名' : 'Name'}</Label>
                                <Input 
                                    id="name" 
                                    value={userName} 
                                    onChange={function(e) { setUserName(e.target.value); }}
                                    placeholder={language === 'zh' ? '您的姓名' : 'Your name'} 
                                    data-testid="input-name" 
                                />
                            </div>
                            <div>
                                <Label>{language === 'zh' ? '性別' : 'Gender'}</Label>
                                <Select value={gender} onValueChange={setGender}>
                                    <SelectTrigger data-testid="gender-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="male">{language === 'zh' ? '男' : 'Male'}</SelectItem>
                                        <SelectItem value="female">{language === 'zh' ? '女' : 'Female'}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div>
                            <Label>{language === 'zh' ? '出生日期' : 'Date of Birth'}</Label>
                            <div className="grid grid-cols-3 gap-2 mt-1">
                                <Select value={birthYear} onValueChange={setBirthYear}>
                                    <SelectTrigger data-testid="birth-year-select">
                                        <SelectValue placeholder="Year" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {YEARS.map(function(yr) {
                                            return <SelectItem key={yr} value={yr}>{yr}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                                <Select value={birthMonth} onValueChange={setBirthMonth}>
                                    <SelectTrigger data-testid="birth-month-select">
                                        <SelectValue placeholder="Month" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MONTHS.map(function(mon) {
                                            return <SelectItem key={mon} value={mon}>{mon}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                                <Select value={birthDay} onValueChange={setBirthDay}>
                                    <SelectTrigger data-testid="birth-day-select">
                                        <SelectValue placeholder="Day" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {DAYS.map(function(dy) {
                                            return <SelectItem key={dy} value={dy}>{dy}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div>
                            <Label>{language === 'zh' ? '出生時間' : 'Time of Birth'}</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                <Select value={birthHour} onValueChange={setBirthHour} disabled={timeUnknown}>
                                    <SelectTrigger data-testid="birth-hour-select">
                                        <SelectValue placeholder="Hour" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {HOURS.map(function(hr) {
                                            return <SelectItem key={hr} value={hr}>{hr}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                                <Select value={birthMinute} onValueChange={setBirthMinute} disabled={timeUnknown}>
                                    <SelectTrigger data-testid="birth-minute-select">
                                        <SelectValue placeholder="Min" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {MINUTES.map(function(min) {
                                            return <SelectItem key={min} value={min}>{min}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <Checkbox 
                                    id="time-unknown" 
                                    checked={timeUnknown} 
                                    onCheckedChange={setTimeUnknown}
                                    data-testid="time-unknown-checkbox"
                                />
                                <label htmlFor="time-unknown" className="text-sm text-muted-foreground cursor-pointer">
                                    {language === 'zh' ? '出生時間不詳' : 'Time of Birth Unknown'}
                                </label>
                            </div>
                        </div>
                        
                        <div>
                            <Label>{language === 'zh' ? '活動類型' : 'Activity Type'}</Label>
                            <Select value={activityType} onValueChange={handleActivityChange}>
                                <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ACTIVITY_TYPES.map(function(a) {
                                        return (
                                            <SelectItem key={a.id} value={a.id}>
                                                <span className="flex items-center gap-2"><span>{a.icon}</span><span>{language === 'zh' ? a.chinese : a.english}</span></span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <p className="text-xs text-muted-foreground italic">
                            {language === 'zh' 
                                ? '*請輸入西曆出生日期，系統將自動轉換為中國農曆和節氣計算八字。'
                                : '*Please enter your Gregorian date of birth. The system will automatically convert to Chinese Solar Calendar for BaZi calculation.'}
                        </p>
                        
                        {error && (
                            <div className="text-red-500 text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />{error}
                            </div>
                        )}
                        <Button onClick={calculateBazi} className="w-full" disabled={loading} data-testid="calculate-bazi-btn">
                            {loading ? (
                                <span className="flex items-center gap-2"><span className="animate-spin">⌛</span>{language === 'zh' ? '計算中...' : 'Calculating...'}</span>
                            ) : (
                                <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" />{language === 'zh' ? '計算八字' : 'Calculate BaZi'}</span>
                            )}
                        </Button>
                        
                        {/* Save Profile Button (Admin Only) */}
                        {isAdmin && (
                            <Button 
                                onClick={saveCurrentProfile} 
                                variant="outline" 
                                className="w-full flex items-center gap-2" 
                                disabled={savingProfile || !userName.trim()}
                                data-testid="save-profile-btn"
                            >
                                <Save className="w-4 h-4" />
                                {savingProfile ? 'Saving...' : 'Save Profile'}
                            </Button>
                        )}
                        
                        {personalBazi && (
                            <div className="mt-4 p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border" data-testid="bazi-chart">
                                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                    <Star className="w-4 h-4 text-yellow-500" />
                                    {language === 'zh' ? '您的八字' : 'Your Four Pillars'}
                                </h3>
                                <div className="grid grid-cols-4 gap-2 text-center mb-4">
                                    <div className="bg-background rounded-lg p-2 border shadow-sm">
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{language === 'zh' ? '時柱' : 'Hour'}</div>
                                        <div className="font-chinese text-xl font-bold mt-1">{personalBazi.pillars.hour.combined_chinese}</div>
                                        <div className="text-[10px] text-muted-foreground mt-1">{personalBazi.pillars.hour.stem.pinyin}</div>
                                        <div className="text-[9px] text-blue-600 dark:text-blue-400">{personalBazi.pillars.hour.stem.english}</div>
                                    </div>
                                    <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-2 border border-amber-200 dark:border-amber-700 shadow-sm">
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{language === 'zh' ? '日柱' : 'Day'}</div>
                                        <div className="font-chinese text-xl font-bold text-primary mt-1">{personalBazi.pillars.day.combined_chinese}</div>
                                        <div className="text-[10px] text-muted-foreground mt-1">{personalBazi.pillars.day.stem.pinyin}</div>
                                        <div className="text-[9px] text-orange-600 dark:text-orange-400">{personalBazi.pillars.day.stem.english}</div>
                                    </div>
                                    <div className="bg-background rounded-lg p-2 border shadow-sm">
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{language === 'zh' ? '月柱' : 'Month'}</div>
                                        <div className="font-chinese text-xl font-bold mt-1">{personalBazi.pillars.month.combined_chinese}</div>
                                        <div className="text-[10px] text-muted-foreground mt-1">{personalBazi.pillars.month.stem.pinyin}</div>
                                        <div className="text-[9px] text-blue-600 dark:text-blue-400">{personalBazi.pillars.month.stem.english}</div>
                                    </div>
                                    <div className="bg-background rounded-lg p-2 border shadow-sm">
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{language === 'zh' ? '年柱' : 'Year'}</div>
                                        <div className="font-chinese text-xl font-bold mt-1">{personalBazi.pillars.year.combined_chinese}</div>
                                        <div className="text-[10px] text-muted-foreground mt-1">{personalBazi.pillars.year.stem.pinyin}</div>
                                        <div className="text-[9px] text-blue-600 dark:text-blue-400">{personalBazi.pillars.year.stem.english}</div>
                                    </div>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between items-center p-2 bg-background rounded">
                                        <span className="text-muted-foreground">{language === 'zh' ? '日主' : 'Day Master'}</span>
                                        <span className="font-chinese font-bold">{personalBazi.day_master.chinese} ({personalBazi.day_master.element})</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                                        <span className="text-muted-foreground">{language === 'zh' ? '貴人' : 'Nobleman'}</span>
                                        <span className="font-medium text-emerald-700 dark:text-emerald-300">{personalBazi.nobleman_animals.join(', ')}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                                        <span className="text-muted-foreground">{language === 'zh' ? '沖' : 'Clash'}</span>
                                        <span className="font-medium text-red-700 dark:text-red-300">{personalBazi.personal_clash.animal} ({personalBazi.personal_clash.chinese})</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <Button variant="outline" size="icon" onClick={function() { handleMonthChange(-1); }} data-testid="prev-month-btn">
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <div className="text-center">
                                <CardTitle className="text-lg">{format(targetDate, 'MMMM yyyy')}</CardTitle>
                                {solarMonthInfo && (
                                    <div className="text-sm text-muted-foreground mt-1">
                                        <span className="font-chinese">{solarMonthInfo.chinese_name}</span> {solarMonthInfo.english_name}
                                    </div>
                                )}
                            </div>
                            <Button variant="outline" size="icon" onClick={function() { handleMonthChange(1); }} data-testid="next-month-btn">
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {!personalBazi ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p className="font-medium">{language === 'zh' ? '請先輸入出生資料' : 'Please enter birth information first'}</p>
                                <p className="text-sm mt-2">{language === 'zh' ? '填寫左側表單後點擊計算八字' : 'Fill the form on the left and click Calculate BaZi'}</p>
                            </div>
                        ) : loading ? (
                            <div className="text-center py-12">
                                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                                <p className="text-muted-foreground">{language === 'zh' ? '加載中...' : 'Loading...'}</p>
                            </div>
                        ) : (
                            <div>
                                <div className="flex flex-wrap gap-3 mb-4 text-xs">
                                    <div className="flex items-center gap-1.5"><div className={'w-3 h-3 rounded ' + getRatingColor('excellent')} /><span>上吉 Excellent</span></div>
                                    <div className="flex items-center gap-1.5"><div className={'w-3 h-3 rounded ' + getRatingColor('good')} /><span>中吉 Good</span></div>
                                    <div className="flex items-center gap-1.5"><div className={'w-3 h-3 rounded ' + getRatingColor('neutral')} /><span>平 Neutral</span></div>
                                    <div className="flex items-center gap-1.5"><div className={'w-3 h-3 rounded ' + getRatingColor('poor')} /><span>凶 Poor</span></div>
                                    <div className="flex items-center gap-1.5"><div className={'w-3 h-3 rounded ' + getRatingColor('avoid')} /><span>大凶 Avoid</span></div>
                                </div>
                                {renderCalendar()}
                                {renderSummary()}
                                {renderBestDays()}
                                <div className="mt-6 flex justify-end gap-2">
                                    <Button onClick={exportToPDF} variant="default" className="gap-2" disabled={exporting} data-testid="export-pdf-btn">
                                        {exporting ? (
                                            <span className="flex items-center gap-2"><span className="animate-spin">⌛</span>{language === 'zh' ? '導出中...' : 'Exporting...'}</span>
                                        ) : (
                                            <span className="flex items-center gap-2"><FileText className="w-4 h-4" />{language === 'zh' ? '導出PDF報告' : 'Export PDF Report'}</span>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {renderDayDialog()}
        </div>
    );
}

export default PersonalDateSelection;
